import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Search, MapPin, Star, Wifi, Car, Coffee, Dumbbell, 
  Waves, Utensils, Shield, ExternalLink, ChevronDown, ChevronUp,
  Building2, Bed, Bath, Users, Calendar, ArrowRight, TrendingDown,
  Filter, SortAsc, Grid3X3, List, Info, AlertCircle, CheckCircle2,
  Globe, Loader2, Image as ImageIcon, X, Plane, Hotel, ShoppingCart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All available destinations with search keywords
const allDestinations = [
  { name: 'Barcelona', code: 'BCN', country: 'Spain', region: 'Europe', keywords: ['spain', 'catalan', 'mediterranean'] },
  { name: 'Paris', code: 'PAR', country: 'France', region: 'Europe', keywords: ['france', 'french', 'eiffel'] },
  { name: 'London', code: 'LON', country: 'United Kingdom', region: 'Europe', keywords: ['uk', 'england', 'british'] },
  { name: 'Rome', code: 'ROM', country: 'Italy', region: 'Europe', keywords: ['italy', 'italian', 'vatican'] },
  { name: 'Amsterdam', code: 'AMS', country: 'Netherlands', region: 'Europe', keywords: ['dutch', 'holland', 'netherlands'] },
  { name: 'Madrid', code: 'MAD', country: 'Spain', region: 'Europe', keywords: ['spain', 'spanish'] },
  { name: 'Berlin', code: 'BER', country: 'Germany', region: 'Europe', keywords: ['german', 'germany'] },
  { name: 'Vienna', code: 'VIE', country: 'Austria', region: 'Europe', keywords: ['austrian', 'austria'] },
  { name: 'Prague', code: 'PRG', country: 'Czech Republic', region: 'Europe', keywords: ['czech', 'bohemia'] },
  { name: 'Budapest', code: 'BUD', country: 'Hungary', region: 'Europe', keywords: ['hungarian', 'hungary'] },
  { name: 'Lisbon', code: 'LIS', country: 'Portugal', region: 'Europe', keywords: ['portuguese', 'portugal'] },
  { name: 'Athens', code: 'ATH', country: 'Greece', region: 'Europe', keywords: ['greek', 'greece'] },
  { name: 'Dubai', code: 'DXB', country: 'UAE', region: 'Middle East', keywords: ['emirates', 'arab', 'uae'] },
  { name: 'Istanbul', code: 'IST', country: 'Turkey', region: 'Middle East', keywords: ['turkish', 'turkey'] },
  { name: 'Bangkok', code: 'BKK', country: 'Thailand', region: 'Asia', keywords: ['thai', 'thailand'] },
  { name: 'Tokyo', code: 'TYO', country: 'Japan', region: 'Asia', keywords: ['japanese', 'japan'] },
  { name: 'Singapore', code: 'SIN', country: 'Singapore', region: 'Asia', keywords: ['singaporean'] },
  { name: 'Hong Kong', code: 'HKG', country: 'Hong Kong', region: 'Asia', keywords: ['chinese', 'china'] },
  { name: 'Bali', code: 'DPS', country: 'Indonesia', region: 'Asia', keywords: ['indonesian', 'indonesia', 'denpasar'] },
  { name: 'Phuket', code: 'HKT', country: 'Thailand', region: 'Asia', keywords: ['thai', 'thailand', 'beach'] },
  { name: 'Helsinki', code: 'HEL', country: 'Finland', region: 'Europe', keywords: ['finnish', 'finland', 'nordic'] },
  { name: 'Stockholm', code: 'STO', country: 'Sweden', region: 'Europe', keywords: ['swedish', 'sweden', 'nordic'] },
  { name: 'Oslo', code: 'OSL', country: 'Norway', region: 'Europe', keywords: ['norwegian', 'norway', 'nordic'] },
  { name: 'Copenhagen', code: 'CPH', country: 'Denmark', region: 'Europe', keywords: ['danish', 'denmark', 'nordic'] },
  { name: 'New York', code: 'NYC', country: 'USA', region: 'North America', keywords: ['american', 'usa', 'manhattan'] },
  { name: 'Los Angeles', code: 'LAX', country: 'USA', region: 'North America', keywords: ['american', 'usa', 'california'] },
  { name: 'Miami', code: 'MIA', country: 'USA', region: 'North America', keywords: ['american', 'usa', 'florida', 'beach'] },
  { name: 'Las Vegas', code: 'LAS', country: 'USA', region: 'North America', keywords: ['american', 'usa', 'nevada', 'casino'] },
  { name: 'Sydney', code: 'SYD', country: 'Australia', region: 'Oceania', keywords: ['australian', 'australia'] },
  { name: 'Melbourne', code: 'MEL', country: 'Australia', region: 'Oceania', keywords: ['australian', 'australia'] },
  { name: 'Nairobi', code: 'NBO', country: 'Kenya', region: 'Africa', keywords: ['kenyan', 'kenya', 'safari'] },
  { name: 'Cape Town', code: 'CPT', country: 'South Africa', region: 'Africa', keywords: ['south african', 'safari'] },
  { name: 'Marrakech', code: 'RAK', country: 'Morocco', region: 'Africa', keywords: ['moroccan', 'morocco'] },
  { name: 'Cairo', code: 'CAI', country: 'Egypt', region: 'Africa', keywords: ['egyptian', 'egypt', 'pyramids'] },
  { name: 'Zanzibar', code: 'ZNZ', country: 'Tanzania', region: 'Africa', keywords: ['tanzanian', 'tanzania', 'beach'] },
  { name: 'Maldives', code: 'MLE', country: 'Maldives', region: 'Asia', keywords: ['maldivian', 'beach', 'resort'] },
  { name: 'Cancun', code: 'CUN', country: 'Mexico', region: 'North America', keywords: ['mexican', 'mexico', 'beach'] },
  { name: 'Rio de Janeiro', code: 'GIG', country: 'Brazil', region: 'South America', keywords: ['brazilian', 'brazil'] },
  { name: 'Buenos Aires', code: 'BUE', country: 'Argentina', region: 'South America', keywords: ['argentinian', 'argentina'] },
];

// Amenity icons mapping
const amenityIcons = {
  'Free WiFi': Wifi,
  'WiFi': Wifi,
  'Pool': Waves,
  'Swimming Pool': Waves,
  'Infinity Pool': Waves,
  'Rooftop Pool': Waves,
  'Spa': Shield,
  'Restaurant': Utensils,
  'Fine Dining': Utensils,
  'Gym': Dumbbell,
  'Fitness Center': Dumbbell,
  'Parking': Car,
  'Valet Parking': Car,
  'Bar': Coffee,
  'Rooftop Bar': Coffee,
  'Terrace Bar': Coffee
};

// Source colors for OTAs
const sourceColors = {
  'Booking.com': 'bg-blue-600',
  'Expedia': 'bg-yellow-500',
  'Hotels.com': 'bg-red-600',
  'Agoda': 'bg-purple-600',
  'TripAdvisor': 'bg-green-600',
  'Marriott Direct': 'bg-gray-800',
  'Direct Hotel': 'bg-gray-600',
  'Amadeus Direct': 'bg-indigo-600'
};

// Popular destinations for quick selection
const popularDestinations = allDestinations.slice(0, 12);

const HotelComparison = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    destination: '',
    checkIn: '',
    checkOut: '',
    adults: 2,
    rooms: 1
  });
  const [results, setResults] = useState(null);
  const [expandedHotel, setExpandedHotel] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('price_low');
  const [apiStatus, setApiStatus] = useState(null);
  
  // Location autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Booking modal state
  const [bookingModal, setBookingModal] = useState({
    open: false,
    hotel: null,
    selectedPrice: null,
    selectedRoom: null
  });

  // Filter destinations based on input
  const filterDestinations = useCallback((query) => {
    if (!query || query.length < 1) {
      setFilteredDestinations([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const filtered = allDestinations.filter(dest => 
      dest.name.toLowerCase().includes(lowerQuery) ||
      dest.country.toLowerCase().includes(lowerQuery) ||
      dest.code.toLowerCase().includes(lowerQuery) ||
      dest.region.toLowerCase().includes(lowerQuery) ||
      dest.keywords.some(kw => kw.includes(lowerQuery))
    ).slice(0, 8); // Limit to 8 suggestions
    
    setFilteredDestinations(filtered);
  }, []);

  // Handle destination input change
  const handleDestinationChange = (e) => {
    const value = e.target.value;
    setSearchParams(prev => ({ ...prev, destination: value }));
    filterDestinations(value);
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  // Handle destination selection
  const selectDestination = (dest) => {
    setSearchParams(prev => ({ ...prev, destination: dest.name }));
    setShowSuggestions(false);
    setFilteredDestinations([]);
  };

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredDestinations.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredDestinations.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredDestinations[highlightedIndex]) {
          selectDestination(filteredDestinations[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open booking modal
  const openBookingModal = (hotel, price, room) => {
    setBookingModal({
      open: true,
      hotel,
      selectedPrice: price,
      selectedRoom: room
    });
  };

  // Handle booking confirmation
  const handleBooking = () => {
    const { hotel, selectedPrice, selectedRoom } = bookingModal;
    
    if (selectedPrice?.booking_url) {
      // Open OTA booking page
      window.open(selectedPrice.booking_url, '_blank');
      toast.success(`Redirecting to ${selectedPrice.source} for booking...`);
    } else {
      // Internal booking (contracted rate)
      toast.success(`Booking request sent for ${hotel.name}!`, {
        description: `Room: ${selectedRoom || 'Standard'} - €${selectedPrice?.price}`
      });
    }
    
    setBookingModal({ open: false, hotel: null, selectedPrice: null, selectedRoom: null });
  };

  // Set default dates (30 days from now, 3 nights)
  useEffect(() => {
    const today = new Date();
    const checkIn = new Date(today.setDate(today.getDate() + 30));
    const checkOut = new Date(today.setDate(today.getDate() + 3));
    
    setSearchParams(prev => ({
      ...prev,
      checkIn: checkIn.toISOString().split('T')[0],
      checkOut: checkOut.toISOString().split('T')[0]
    }));
  }, []);

  // Check API status on load
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/hotels/api-status`);
        if (response.ok) {
          const data = await response.json();
          setApiStatus(data);
        }
      } catch (error) {
        console.log('API status check failed');
      }
    };
    checkApiStatus();
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchParams.destination) {
      toast.error('Please enter a destination');
      return;
    }
    if (!searchParams.checkIn || !searchParams.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams({
        destination: searchParams.destination,
        check_in: searchParams.checkIn,
        check_out: searchParams.checkOut,
        adults: searchParams.adults,
        rooms: searchParams.rooms
      });

      let data = null;
      
      // Try live search first if authenticated
      if (token && apiStatus?.amadeus?.configured) {
        try {
          const liveResponse = await fetch(`${API_URL}/api/hotels/search?${params}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (liveResponse.ok) {
            data = await liveResponse.json();
            if (data.total_results > 0) {
              toast.success(`Found ${data.total_results} hotels with live pricing!`);
            }
          }
        } catch (liveError) {
          console.log('Live search failed, falling back to demo');
        }
      }

      // Fallback to demo if live search failed or returned no results
      if (!data || data.total_results === 0) {
        const demoResponse = await fetch(`${API_URL}/api/hotels/demo-search?${params}`);
        if (demoResponse.ok) {
          data = await demoResponse.json();
          if (data.is_demo) {
            toast.info('Amadeus test API has limited data. Showing demo prices for comparison.', {
              duration: 5000,
              description: 'Production API needed for full hotel inventory.'
            });
          }
        }
      }

      if (data) {
        setResults(data);
      } else {
        throw new Error('No results found');
      }
    } catch (error) {
      toast.error('Search failed. Please try again.');
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickDestination = (dest) => {
    setSearchParams(prev => ({ ...prev, destination: dest.name }));
  };

  // Sort results
  const getSortedResults = () => {
    if (!results?.results) return [];
    
    const sorted = [...results.results];
    switch (sortBy) {
      case 'price_low':
        return sorted.sort((a, b) => (a.lowest_price || 999999) - (b.lowest_price || 999999));
      case 'price_high':
        return sorted.sort((a, b) => (b.lowest_price || 0) - (a.lowest_price || 0));
      case 'rating':
        return sorted.sort((a, b) => (b.hotel.user_rating || 0) - (a.hotel.user_rating || 0));
      case 'savings':
        return sorted.sort((a, b) => (b.savings_percent || 0) - (a.savings_percent || 0));
      default:
        return sorted;
    }
  };

  // Calculate nights
  const calculateNights = () => {
    if (!searchParams.checkIn || !searchParams.checkOut) return 0;
    const start = new Date(searchParams.checkIn);
    const end = new Date(searchParams.checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary" />
          Hotel Price Comparison
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Compare hotel prices across multiple booking platforms and your contracted rates
        </p>
      </div>

      {/* API Status Banner */}
      {apiStatus?.demo_mode && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <span className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Demo Mode:</strong> Using sample data. Configure Amadeus/Xotelo API keys for live hotel search.
          </span>
        </div>
      )}
      
      {/* Amadeus Test Mode Banner */}
      {apiStatus && !apiStatus.demo_mode && apiStatus.amadeus?.configured && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-blue-600" />
          <span className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Amadeus Connected (Test Mode):</strong> Using Amadeus sandbox API. Limited hotel data available - demo prices shown when live data unavailable.
          </span>
        </div>
      )}

      {/* Search Form */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Destination with Autocomplete */}
              <div className="lg:col-span-1 relative">
                <Label htmlFor="destination">Destination</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                  <Input
                    ref={inputRef}
                    id="destination"
                    placeholder="Search city or country..."
                    value={searchParams.destination}
                    onChange={handleDestinationChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      if (searchParams.destination) {
                        filterDestinations(searchParams.destination);
                        setShowSuggestions(true);
                      }
                    }}
                    className="pl-10"
                    autoComplete="off"
                    data-testid="destination-input"
                  />
                  {searchParams.destination && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchParams(prev => ({ ...prev, destination: '' }));
                        setFilteredDestinations([]);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                {/* Autocomplete Suggestions Dropdown */}
                {showSuggestions && filteredDestinations.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                  >
                    {filteredDestinations.map((dest, index) => (
                      <button
                        key={dest.code}
                        type="button"
                        onClick={() => selectDestination(dest)}
                        className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          index === highlightedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
                        }`}
                      >
                        <div className="flex-shrink-0">
                          {dest.region === 'Europe' && <Globe className="h-4 w-4 text-blue-500" />}
                          {dest.region === 'Asia' && <Plane className="h-4 w-4 text-green-500" />}
                          {dest.region === 'North America' && <Building2 className="h-4 w-4 text-purple-500" />}
                          {dest.region === 'Africa' && <MapPin className="h-4 w-4 text-orange-500" />}
                          {dest.region === 'Middle East' && <Hotel className="h-4 w-4 text-amber-500" />}
                          {dest.region === 'Oceania' && <Waves className="h-4 w-4 text-cyan-500" />}
                          {dest.region === 'South America' && <MapPin className="h-4 w-4 text-red-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {dest.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {dest.country} • {dest.region}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {dest.code}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Check-in */}
              <div>
                <Label htmlFor="checkIn">Check-in</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="checkIn"
                    type="date"
                    value={searchParams.checkIn}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, checkIn: e.target.value }))}
                    className="pl-10"
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="checkin-input"
                  />
                </div>
              </div>

              {/* Check-out */}
              <div>
                <Label htmlFor="checkOut">Check-out</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="checkOut"
                    type="date"
                    value={searchParams.checkOut}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, checkOut: e.target.value }))}
                    className="pl-10"
                    min={searchParams.checkIn}
                    data-testid="checkout-input"
                  />
                </div>
              </div>

              {/* Guests */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="adults">Adults</Label>
                  <Select
                    value={String(searchParams.adults)}
                    onValueChange={(v) => setSearchParams(prev => ({ ...prev, adults: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="adults-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label htmlFor="rooms">Rooms</Label>
                  <Select
                    value={String(searchParams.rooms)}
                    onValueChange={(v) => setSearchParams(prev => ({ ...prev, rooms: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="rooms-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={loading}
                  data-testid="search-hotels-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Compare Prices
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Quick Destination Chips */}
            <div className="flex flex-wrap gap-2 pt-2">
              <span className="text-xs text-gray-500 mr-2 self-center">Popular:</span>
              {popularDestinations.slice(0, 8).map(dest => (
                <button
                  key={dest.code}
                  type="button"
                  onClick={() => handleQuickDestination(dest)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    searchParams.destination === dest.name 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white dark:bg-gray-800 hover:border-primary'
                  }`}
                >
                  {dest.name}
                </button>
              ))}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results Section */}
      {results && (
        <>
          {/* Results Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {results.total_results} hotels found in {results.destination}
              </h2>
              <p className="text-sm text-gray-500">
                {searchParams.checkIn} → {searchParams.checkOut} ({calculateNights()} nights) • {searchParams.adults} adults • {searchParams.rooms} room(s)
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SortAsc className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Guest Rating</SelectItem>
                  <SelectItem value="savings">Best Savings</SelectItem>
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800'}`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Data Sources */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">Prices from:</span>
            {results.sources?.map(source => (
              <Badge 
                key={source} 
                variant="outline" 
                className={`text-xs text-white ${sourceColors[source] || 'bg-gray-500'}`}
              >
                {source}
              </Badge>
            ))}
            {results.is_demo && (
              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                Demo Data
              </Badge>
            )}
          </div>

          {/* Hotel Cards */}
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : 'space-y-4'}>
            {getSortedResults().map((result) => (
              <HotelCard 
                key={result.hotel.hotel_id}
                result={result}
                nights={calculateNights()}
                expanded={expandedHotel === result.hotel.hotel_id}
                onToggle={() => setExpandedHotel(
                  expandedHotel === result.hotel.hotel_id ? null : result.hotel.hotel_id
                )}
                viewMode={viewMode}
              />
            ))}
          </div>
        </>
      )}

      {/* Empty State */}
      {!results && !loading && (
        <Card className="p-12 text-center">
          <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Search for Hotels
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Enter a destination and dates to compare hotel prices across multiple booking platforms.
            See your contracted rates vs online prices side by side.
          </p>
        </Card>
      )}
    </div>
  );
};

// Hotel Card Component
const HotelCard = ({ result, nights, expanded, onToggle, viewMode }) => {
  const { hotel, prices, lowest_price, highest_price, contracted_price, savings_percent } = result;
  
  const pricePerNight = lowest_price ? (lowest_price / nights).toFixed(0) : null;

  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-shadow ${viewMode === 'list' ? 'flex flex-col md:flex-row' : ''}`}>
      {/* Hotel Image */}
      <div className={`relative ${viewMode === 'list' ? 'md:w-72 h-48 md:h-auto' : 'h-48'}`}>
        {hotel.photos?.[0] ? (
          <img 
            src={hotel.photos[0]} 
            alt={hotel.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <ImageIcon className="h-12 w-12 text-gray-400" />
          </div>
        )}
        
        {/* Star Rating Badge */}
        {hotel.star_rating && (
          <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-md flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-medium">{hotel.star_rating}</span>
          </div>
        )}

        {/* Contracted Rate Badge */}
        {contracted_price && (
          <div className="absolute top-3 right-3 bg-green-600 text-white px-2 py-1 rounded-md text-xs font-medium">
            Contract Rate Available
          </div>
        )}
      </div>

      {/* Hotel Info */}
      <div className="flex-1 p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white line-clamp-1">
              {hotel.name}
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {hotel.address || hotel.city}, {hotel.country}
            </p>
          </div>
          
          {/* User Rating */}
          {hotel.user_rating && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="bg-primary text-white px-2 py-1 rounded-md text-sm font-bold">
                    {hotel.user_rating}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{hotel.review_count?.toLocaleString()} reviews</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Amenities */}
        {hotel.amenities?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {hotel.amenities.slice(0, 5).map((amenity, i) => {
              const IconComponent = amenityIcons[amenity] || CheckCircle2;
              return (
                <span key={i} className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  <IconComponent className="h-3 w-3" />
                  {amenity}
                </span>
              );
            })}
            {hotel.amenities.length > 5 && (
              <span className="text-xs text-gray-500">+{hotel.amenities.length - 5} more</span>
            )}
          </div>
        )}

        {/* Room Info */}
        {hotel.room_types?.[0] && (
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
            <span className="flex items-center gap-1">
              <Bed className="h-3 w-3" />
              {hotel.room_types[0].type}
            </span>
            {hotel.room_types[0].size_sqm && (
              <span>{hotel.room_types[0].size_sqm} m²</span>
            )}
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              Max {hotel.room_types[0].max_occupancy}
            </span>
            {hotel.room_types[0].bathrooms && (
              <span className="flex items-center gap-1">
                <Bath className="h-3 w-3" />
                {hotel.room_types[0].bathrooms} bath
              </span>
            )}
          </div>
        )}

        {/* Price Summary */}
        <div className="flex items-end justify-between border-t pt-3 mt-3">
          <div>
            <div className="flex items-baseline gap-2">
              {contracted_price && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500">Your Contract</span>
                  <span className="text-lg font-bold text-green-600">€{contracted_price}</span>
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Best Online</span>
                <span className={`text-lg font-bold ${contracted_price ? 'text-gray-400 line-through' : 'text-primary'}`}>
                  €{lowest_price}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {nights} nights • €{pricePerNight}/night
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            {savings_percent && (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <TrendingDown className="h-3 w-3 mr-1" />
                Save {savings_percent}%
              </Badge>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={onToggle}
              data-testid={`compare-prices-${hotel.hotel_id}`}
            >
              {expanded ? 'Hide Prices' : 'Compare All Prices'}
              {expanded ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>

        {/* Expanded Price Comparison Table */}
        {expanded && (
          <div className="mt-4 border-t pt-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Price Comparison ({prices.length} sources)
            </h4>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">Source</th>
                    <th className="text-left py-2 font-medium">Room Type</th>
                    <th className="text-center py-2 font-medium">Breakfast</th>
                    <th className="text-left py-2 font-medium">Cancellation</th>
                    <th className="text-right py-2 font-medium">Total</th>
                    <th className="text-right py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Contracted Rate Row */}
                  {contracted_price && (
                    <tr className="border-b bg-green-50 dark:bg-green-900/20">
                      <td className="py-2">
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <strong>Your Contract</strong>
                        </span>
                      </td>
                      <td className="py-2">As per agreement</td>
                      <td className="py-2 text-center">
                        <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
                      </td>
                      <td className="py-2 text-green-600">Flexible</td>
                      <td className="py-2 text-right font-bold text-green-600">€{contracted_price}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700">
                          Book Direct
                        </Button>
                      </td>
                    </tr>
                  )}
                  
                  {/* OTA Price Rows */}
                  {prices
                    .sort((a, b) => a.price - b.price)
                    .map((price, idx) => (
                    <tr key={idx} className={`border-b ${idx === 0 && !contracted_price ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                      <td className="py-2">
                        <span className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${sourceColors[price.source] || 'bg-gray-400'}`}></div>
                          {price.source}
                          {idx === 0 && !contracted_price && (
                            <Badge variant="outline" className="text-xs ml-1">Best</Badge>
                          )}
                        </span>
                      </td>
                      <td className="py-2 text-gray-600">{price.room_type || 'Standard'}</td>
                      <td className="py-2 text-center">
                        {price.breakfast_included ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2 text-xs text-gray-500 max-w-[150px] truncate">
                        {price.cancellation_policy || 'Check policy'}
                      </td>
                      <td className="py-2 text-right font-semibold">
                        €{price.price}
                        {contracted_price && price.price > contracted_price && (
                          <span className="text-xs text-red-500 block">
                            +€{(price.price - contracted_price).toFixed(0)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {price.booking_url ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(price.booking_url, '_blank')}
                          >
                            Book
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Price Range Summary */}
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Price range: <strong>€{lowest_price}</strong> - <strong>€{highest_price}</strong>
                </span>
                {contracted_price && (
                  <span className="text-green-600 font-medium">
                    Contract saves up to €{(highest_price - contracted_price).toFixed(0)} ({((1 - contracted_price/highest_price) * 100).toFixed(0)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default HotelComparison;
